import { ReactElement, useRef, useState } from "react";
import { useMount, useRequest, useSetState } from "ahooks";
import { message, Select } from "antd";
import AppSetting from "../../../config/AppSetting";

import NormalSpin from "../../../base/basicComponents/spin";
import TopFilter from "../../sections/myRequestListSections/topFilter/index";
import SearchAndColumnSelect from "../procedureList/procedureListSections/searchAndColumnSelect";
import MyRequestListColumnSelectOption, {
  ColumnsVisibleKey,
  ColumnsVisibleType,
  MyRequestListColumnSelectValue,
} from "../../sections/myRequestListSections/columnSelectOption";
import ListTable from "../../commonComponents/listTable/index";
import MyRequestListColumn from "../../sections/myRequestListSections/myRequestListColumn";
import MyRequestTableFilterMap from "../../commonComponents/listTable/myRequestListFilterMap";
import FooterPagination from "../procedureList/procedureListSections/footerPagination";
import myRequestListService from "../../../service/myRequestListService/index";
import styles from "./index.module.scss";
import { handleUserRole } from "utils";
import { getFilterResult, getInitFilterData, getInitPayloadData } from "./utils";
import {
  FilterOptionList,
  MyRequestTableFilterNameLower,
  MyRequestTableFilterName,
  SelectOptionList,
} from "app/sections/myRequestListSections/types";
import {
  IRequestListFilter,
  MyRequestListPayload,
  MyRequestListResponse,
  MyRequestTableFilterResponse,
  NeedDeduplication,
} from "service/myRequestListService/types";
import { createFakeProgressGenerator } from "../procedureList/utils";
import { getDate, getTimeZoneString } from "app/publicDictionaryValues";
import { downloadPDF } from "app/commonComponents/fileManagementFields/coverSheet";

const columnSelectValue = MyRequestListColumnSelectValue();
const columnSelectOption = MyRequestListColumnSelectOption();
const getFakeProgress = createFakeProgressGenerator(98, 0.05);
let timer: NodeJS.Timeout | null = null;

export default function MyRequestList() {
  const searchAndColumnSelectRef = useRef<any>(null);
  const selectRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  // useRef to avoid multiple fetches when clicking the table column filter button multiple times
  const isFetchedFilterDataRef = useRef(false);

  // we have to save the filter state outside the payload
  const [topInputFilter, setTopInputFilter] = useSetState({
    taskLabelName: "",
    taskId: "",
    transactionId: "",
  });

  const [pagenation, setPagination] = useSetState({
    pageIndex: 1,
    pageSize: 30,
    totalPage: 0,
    totalRecord: 0,
  });
  //table data
  const [myRequestListData, setMyRequestListData] = useState<MyRequestListResponse["data"]>([]);
  //payload for table data request
  const [myRequestPayload, setMyRequestPayload] =
    useSetState<Omit<MyRequestListPayload, "pageIndex" | "pageSize">>(getInitPayloadData());
  //table column visible
  const [columnsVisible, setColumnsVisible] = useSetState(
    columnSelectOption.reduce((acc, item) => {
      acc[item.value] = columnSelectValue.includes(item.value);
      return acc;
    }, {} as ColumnsVisibleType)
  );
  // table filter data
  const [myRequestListFilterData, setMyRequestListFilterData] =
    useState<IRequestListFilter>(getInitFilterData());

  //user filter data
  const [filteredInfo, setFilteredInfo] = useState<
    Partial<Record<MyRequestTableFilterNameLower, string[] | number[]>>
  >({});
  //user sort data
  const [tableSortData, setTableSortData] = useState<
    {
      sortColumn: string;
      isAscending: boolean;
    }[]
  >([]);

  const [myRequestListDivHeight, setMyRequestListDivHeight] = useState(0);
  const [myRequestListTableBodyHeight, setMyRequestListTableBodyHeight] = useState(0);

  const isOpex = handleUserRole(AppSetting.localStorageValue.role.get());

  //top filter/clear button click
  const handleTopFilterClick = (
    filterOptionList: FilterOptionList,
    selectOptions: SelectOptionList
  ) => {
    isFetchedFilterDataRef.current = false;
    setFilteredInfo({});
    setTableSortData([]);
    setMyRequestListFilterData(getInitFilterData());
    setTopInputFilter({
      taskLabelName: "",
      taskId: "",
      transactionId: "",
    });
    setPagination({
      pageIndex: 1,
    });
    const payload: typeof myRequestPayload = {
      ...getInitPayloadData(),
      processInitiateDateFrom: selectOptions.processInitiateDateFrom,
      processInitiateDateTo: selectOptions.processInitiateDateTo,
      processScope: selectOptions.processScope,
      accountID: selectOptions.accountAbbr.length
        ? selectOptions.accountAbbr
        : filterOptionList.accountAbbr.map((item) => item.value),
      processStatus: selectOptions.processStatus.length
        ? selectOptions.processStatus
        : filterOptionList.processStatus.map((item) => item.value),
      // if have processCategory, add to payload
      ...(selectOptions.processCategory.length > 0 && {
        filterFields: [
          {
            filterColumn: "processCategory",
            filterValue: selectOptions.processCategory,
          },
        ],
      }),

      sortFields: [],
    };
    setMyRequestPayload(payload);

    runGetMyRequestList({
      ...payload,
      pageIndex: 1,
      pageSize: pagenation.pageSize,
    });
  };

  const { run: runGetMyRequestList, loading: myRequestListLoading } = useRequest(
    myRequestListService.getMyRequestListNew,
    {
      manual: true,
      onSuccess: (result) => {
        setMyRequestListData(result.data);
        setPagination({
          totalPage: result.totalPage,
          totalRecord: result.totalRecord,
        });
      },
      onError: () => {
        message.error("Failed to fetch request list");
      },
    }
  );

  const { run: runGetMyRequestExportFile, loading: exportLoading } = useRequest(
    myRequestListService.getMyRequestExportFile,
    {
      manual: true,
      onBefore: ([params]) => {
        if (params.pageSize === 0) {
          getFakeProgress.reset();
          setProgress(0);
          timer = setInterval(() => {
            const newProgress = getFakeProgress();
            setProgress(newProgress);
          }, 500);
        }
      },
      onSuccess: (result) => {
        setProgress(100);
        clearInterval(timer!);
        const mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const byteCharacters = atob(result);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], { type: mimeType });
        const date = getTimeZoneString(getDate(), "MMDDYYYY");
        downloadPDF(blob, `My Request List - ${date}.xlsx`);
      },
      onError: () => {
        message.error("Failed to download my request list");
      },
      onFinally: () => {
        setTimeout(() => {
          clearInterval(timer!);
        }, 1000);
      },
    }
  );

  const ShowSearchColumn = (payload: typeof myRequestPayload) => {
    let searchColumnArr = [];
    if (payload.transactionId !== "") {
      searchColumnArr.push("transactionIdShow");
    }

    if (payload.taskId !== "") {
      searchColumnArr.push("taskIdShow");
    }

    if (payload.taskLabelName !== "") {
      searchColumnArr.push("taskLabelNameShow");
    }

    searchAndColumnSelectRef.current.handleColumnSearchSelect(searchColumnArr);
  };
  // top search button click
  const handleSearchClick = () => {
    isFetchedFilterDataRef.current = false;
    setFilteredInfo({});
    setTableSortData([]);
    setMyRequestListFilterData(getInitFilterData());
    setPagination({
      pageIndex: 1,
    });

    const payload = {
      ...getInitPayloadData(),
      processInitiateDateFrom: myRequestPayload.processInitiateDateFrom,
      processInitiateDateTo: myRequestPayload.processInitiateDateTo,
      processScope: myRequestPayload.processScope,
      accountID: myRequestPayload.accountID,
      processStatus: myRequestPayload.processStatus,
      filterFields: myRequestPayload.filterFields.filter(
        (item) => item.filterColumn === "processCategory"
      ),

      pageSize: pagenation.pageSize,
      pageIndex: 1,
      taskId: topInputFilter.taskId.trim(),
      taskLabelName: topInputFilter.taskLabelName.trim(),
      transactionId: topInputFilter.transactionId.trim(),
    };
    setMyRequestPayload(payload);

    runGetMyRequestList(payload);

    ShowSearchColumn(payload);
  };

  const handleTransactionIdChange = (e: EventTarget & HTMLInputElement) => {
    setTopInputFilter({
      transactionId: e.value,
    });
  };

  const handleTaskLabelNameChange = (e: EventTarget & HTMLInputElement) => {
    setTopInputFilter({
      taskLabelName: e.value,
    });
  };

  const handleTaskIdChange = (e: EventTarget & HTMLInputElement) => {
    setTopInputFilter({
      taskId: e.value,
    });
  };
  // show selected columns
  const handleColumnSearchSelect = (valueArr: ColumnsVisibleKey[]) => {
    setColumnsVisible({
      ...valueArr.reduce((acc, item) => {
        acc[item] = true;
        return acc;
      }, {} as ColumnsVisibleType),
    });
  };

  // hide unselected columns
  const handleColumnSearchDeSelect = (value: ColumnsVisibleKey) => {
    setColumnsVisible({
      ...columnsVisible,
      [value]: false,
    });
  };

  const handlePageNumberChange = (pageIndex: number, pageSize: number) => {
    const newPageIndex = pageSize === pagenation.pageSize ? pageIndex : 1;
    selectRef.current.blur();
    setPagination({
      pageIndex: newPageIndex,
      pageSize,
    });
    const payload = {
      ...myRequestPayload,
      pageIndex: newPageIndex,
      pageSize,
    };
    runGetMyRequestList(payload);
  };
  //table filter and sort
  const handleTableFilterSort = (
    tableFilterPara: {
      filterColumn: MyRequestTableFilterName;
      filterValue: string[];
    }[],
    tableSortPara: {
      sortColumn: string;
      isAscending: boolean;
    }[],
    filters: Record<MyRequestTableFilterNameLower, string[] | number[]>
  ) => {
    setTableSortData(tableSortPara);
    setFilteredInfo(filters);
    setPagination({
      pageIndex: 1,
    });
    const dropAllSelectRow = getFilterResult(tableFilterPara, myRequestListFilterData);

    const payload: typeof myRequestPayload = {
      ...myRequestPayload,
      sortFields: tableSortPara,
      filterFields: [
        ...myRequestPayload.filterFields.filter((item) => item.filterColumn === "processCategory"),
        ...dropAllSelectRow,
      ],
    };
    setMyRequestPayload(payload);

    runGetMyRequestList({
      ...payload,
      pageIndex: 1,
      pageSize: pagenation.pageSize,
    });
  };

  const handleColumnFilterClick = (flag: boolean) => {
    if (flag && !isFetchedFilterDataRef.current) {
      runGetFilterData({
        ...myRequestPayload,
        // @ts-ignore
        // TODO will remove this prop in the future
        CurrentField: "TaskLabelName",
        filterFields: myRequestPayload.filterFields.filter(
          (item) => item.filterColumn === "processCategory"
        ),
      });
    }
  };

  const { run: runGetFilterData, loading: filterLoading } = useRequest(
    myRequestListService.getMyRequestFilterNew,
    {
      manual: true,
      onSuccess: (result) => {
        const res: IRequestListFilter = getInitFilterData();
        for (const key in result) {
          if (key === "categoryNames") {
            (result[key as NeedDeduplication] ?? []).forEach((option) => {
              option.id = option.name;
            });
          }
          const map = new Map<string, (number | string)[]>();
          (result[key as NeedDeduplication] ?? []).forEach((option) => {
            if (!map.has(option.name)) {
              map.set(option.name, []);
            }
            map.get(option.name)!.push(option.id);
          });
          const deduplicationList = Array.from(map.entries()).map(([name, value]) => ({
            name,
            id: value,
          }));
          res[key as NeedDeduplication] = deduplicationList;
        }

        setMyRequestListFilterData(res);
        isFetchedFilterDataRef.current = true;
      },
      onError: (error) => {
        console.log(error);
        message.error("Failed to fetch filter data");
      },
    }
  );

  const columns = MyRequestListColumn({
    myRequestListFilterData,
    filteredInfo,
    columnsVisible,
    tableSortData,
    handleColumnFilterClick,
    filterLoading,
  });

  const calculateHeight = () => {
    const screenHeight = window.innerHeight;
    const desiredHeight = screenHeight - 114 + 8 + 10;
    setMyRequestListDivHeight(desiredHeight);
    const myRequestListTopFilterDiv = document.getElementById("myRequestListTopFilter");
    if (!myRequestListTopFilterDiv) return;
    const myRequestListTopFilterDivHeight = myRequestListTopFilterDiv.offsetHeight;
    const tableContainer = document.querySelector(".ant-table-body");

    if (tableContainer) {
      const myRequestListTableBodyHeight =
        desiredHeight - myRequestListTopFilterDivHeight - 191 + 8;
      setMyRequestListTableBodyHeight(myRequestListTableBodyHeight);
      (tableContainer as HTMLElement).style.height = `${myRequestListTableBodyHeight}px`;
    }
  };

  useMount(() => {
    calculateHeight();
    window.addEventListener("resize", calculateHeight);

    return () => {
      window.removeEventListener("resize", calculateHeight);
    };
  });

  const totalLoading = myRequestListLoading || loading;

  return (
    <NormalSpin spinning={totalLoading} size="large">
      <div className={styles.procedureListDiv} style={{ height: myRequestListDivHeight }}>
        <div className={styles.procedureTopFilter} id="myRequestListTopFilter">
          <TopFilter
            handleFilterClick={handleTopFilterClick}
            isOpex={isOpex}
            setLoading={setLoading}
          />
          <SearchAndColumnSelect
            ref={searchAndColumnSelectRef}
            columnSelectValue={columnSelectValue}
            columnSelectOption={columnSelectOption}
            handleSearch={handleSearchClick}
            taskLabelName={topInputFilter.taskLabelName}
            taskId={topInputFilter.taskId}
            transactionId={topInputFilter.transactionId}
            handleTransactionIdChange={handleTransactionIdChange}
            handleTaskLabelNameChange={handleTaskLabelNameChange}
            handleTaskIdChange={handleTaskIdChange}
            handleColumnSearchSelect={handleColumnSearchSelect}
            handleColumnSearchDeSelect={handleColumnSearchDeSelect}
            showExport={true}
            exportDisabled={exportLoading}
            progress={progress}
            handleExport={() =>
              runGetMyRequestExportFile({
                ...myRequestPayload,
                pageIndex: 1,
                pageSize: 0,
              })
            }
          />
        </div>
        <div className={styles.tableDiv}>
          <ListTable
            data={myRequestListData}
            columns={columns}
            handleFilterSort={handleTableFilterSort}
            tableFilterMap={MyRequestTableFilterMap}
            tableHeight={myRequestListTableBodyHeight}
            testid="my-request-table"
            rowTestId={(record: any) => `my-request-row-${record.transactionId}`}
          />
          <div className={styles.myRequestFooter}>
            <FooterPagination
              totalRecord={pagenation.totalRecord}
              totalPage={pagenation.totalPage}
              currentPage={{
                pageNumber: pagenation.pageIndex,
                pageSize: pagenation.pageSize,
              }}
              handlePageNumberChange={handlePageNumberChange}
              selectComponentClass={(props: any) => {
                const options = props.children.map((item: ReactElement) => {
                  return {
                    // @ts-ignore
                    label: item.props.children,
                    // @ts-ignore
                    value: item.props.value,
                  };
                });
                return (
                  <Select {...props} options={options} optionLabelProp="label" ref={selectRef} />
                );
              }}
              paginationTestId="my-request-pagination"
            />
          </div>
        </div>
      </div>
    </NormalSpin>
  );
}
